import React, { useEffect, useState } from 'react';

// Types
import { PiSuggestedTopic, PiSuggestedTopicsCategories } from 'Shared/types/PiSuggestedTopic.type';
import { User } from 'Shared/constants/types';

// Hooks
import { usePiSuggestedTopics } from 'Shared/utils/hooks/use-pi-suggested-topics';
import { useTrackPiSuggestedTopics } from 'Shared/tracking/use-track-pi-suggested-topics';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpButton } from 'Wpds/components/button';
import PiSuggestedTopicsListItem from './PiSuggestedTopicsListItem';
import { WpText } from 'Wpds/components/text';
import Spinner from 'Shared/components/Spinner';
import PiSuggestedTopicsCategoryDropdown from './PiSuggestedTopicsCategoryDropdown';

// Styles
import './PiSuggestedTopicsList.scss';

type Props = {
  category: string;
  currentUser: User;
  otherUser: User;
  handleSetCategory: Function;
};

const PiSuggestedTopicsList = ({ category, currentUser, otherUser, handleSetCategory }: Props) => {
  const SET_TIMEOUT_REFRESH_LIMIT = 500;

  const { generalSuggestedTopics, userSuggestedTopics: currentUserSuggestedTopics } = usePiSuggestedTopics({
    user: currentUser,
  });

  const { userSuggestedTopics: otherUserSuggestedTopics } = usePiSuggestedTopics({
    user: otherUser,
  });

  const { trackRefreshClick } = useTrackPiSuggestedTopics();

  // Generates a random number between 0 and the max provided
  // example: randomIntFromInterval(3)
  // Expected output: 0, 1 or 2
  function randomIntFromInterval(max: number) {
    return Math.floor(Math.random() * max);
  }

  const generalTopics =
    category === 'all'
      ? generalSuggestedTopics
      : generalSuggestedTopics.filter(topic => topic.category === PiSuggestedTopicsCategories[category]);

  const currentUserTopics =
    category === 'all'
      ? currentUserSuggestedTopics
      : currentUserSuggestedTopics.filter(topic => topic.category === PiSuggestedTopicsCategories[category]);

  const otherUserTopics =
    category === 'all'
      ? otherUserSuggestedTopics
      : otherUserSuggestedTopics.filter(topic => topic.category === PiSuggestedTopicsCategories[category]);

  const [generalIndex, setGeneralIndex] = useState(randomIntFromInterval(generalTopics.length));
  const [currentUserFactorIndex, setCurrentUserFactorIndex] = useState(randomIntFromInterval(currentUserTopics.length));
  const [otherUserFactorIndex, setOtherUserFactorIndex] = useState(randomIntFromInterval(otherUserTopics.length));
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const topics: PiSuggestedTopic[] = [
    generalTopics[generalIndex],
    currentUserTopics[currentUserFactorIndex],
    otherUserTopics[otherUserFactorIndex],
  ];

  const handleChangeCategory = (value: string) => {
    if (value !== category) {
      setIsRefreshing(true);
      handleSetCategory(value);
    }
  };

  // retuns next availble index and resets to 0 if already at end of the list
  const getNextAvailableIndex = (value: number, list: PiSuggestedTopic[]): number => {
    if (value === list.length - 1) return 0;
    else return value + 1;
  };

  const handleRefreshClick = () => {
    setIsRefreshing(true);
    if (generalIndex === generalTopics.length - 1) setGeneralIndex(0);
    else setGeneralIndex(generalIndex + 1);

    // get next available index for current user topic
    const newCurrentUserFactorIndex = getNextAvailableIndex(currentUserFactorIndex, currentUserTopics);
    // get next topic for current user
    const newCurrentUserTopic = currentUserTopics[newCurrentUserFactorIndex];
    // set the current user index
    setCurrentUserFactorIndex(newCurrentUserFactorIndex);

    // get next index for other user
    let newOtherUserIndex = getNextAvailableIndex(otherUserFactorIndex, otherUserTopics);
    let isSet = false;
    while (!isSet) {
      // get topic at next index
      const newOtherUserTopic = otherUserTopics[newOtherUserIndex];
      // see if it matches the current users next topic
      if (newCurrentUserTopic.suggestion === newOtherUserTopic.suggestion) {
        //if so, iterate to next availble index
        newOtherUserIndex = getNextAvailableIndex(newOtherUserIndex + 1, otherUserTopics);
      } else {
        // otherwise set index for other user
        setOtherUserFactorIndex(newOtherUserIndex);
        isSet = true;
      }
    }

    trackRefreshClick();
    setTimeout(() => setIsRefreshing(false), SET_TIMEOUT_REFRESH_LIMIT);
  };

  useEffect(() => {
    setIsRefreshing(true);
    setGeneralIndex(randomIntFromInterval(generalTopics.length));

    const newCurrentUserIndex = randomIntFromInterval(currentUserTopics.length);
    const newCurrentUserTopic = currentUserTopics[newCurrentUserIndex];
    setCurrentUserFactorIndex(newCurrentUserIndex);

    const newOtherUserIndex = randomIntFromInterval(otherUserTopics.length);
    const newOtherUserTopic = otherUserTopics[newOtherUserIndex];

    // check if the random topics are the same
    if (newCurrentUserTopic.suggestion === newOtherUserTopic.suggestion) {
      // if so, we need to check iterate the index, but also need to check if we're at the end array
      setOtherUserFactorIndex(getNextAvailableIndex(newOtherUserIndex, otherUserTopics));
    } else {
      // otherwise, topics are different, set the new index for the other user list
      setOtherUserFactorIndex(newOtherUserIndex);
    }

    setTimeout(() => setIsRefreshing(false), SET_TIMEOUT_REFRESH_LIMIT);
  }, [category]);

  const baseClassName = 'PiSuggestedTopicsList';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__toolbar`}>
        <PiSuggestedTopicsCategoryDropdown selectedCategory={category} handleSelectCategory={handleChangeCategory} />
        <WpButton onClick={handleRefreshClick} variant="secondary" loading={isRefreshing}>
          <WpIcon name="redo" />
          Refresh
        </WpButton>
      </div>
      <WpText>Use our personalized agenda topics to hold productive 1:1 conversations.</WpText>
      {isRefreshing ? (
        <div className={`${baseClassName}__refreshing`}>
          <Spinner activeIf centered={false} />
          <WpText noMargin>Getting new suggested topics...</WpText>
        </div>
      ) : (
        <>
          {topics.map((topic, index) => (
            <PiSuggestedTopicsListItem topic={topic} key={`key-${index}-${topic.type}`} />
          ))}
        </>
      )}
    </div>
  );
};

export default PiSuggestedTopicsList;
